<template>
  <v-card flat v-if="employment && employment.employed">
    <v-btn class="gray mb-n2 ml-2 mt-n1" text @click="$emit('backToList')">
      <v-icon>$sl-arrow-left</v-icon>Tillbaka</v-btn
    >
    <v-card-title>
      <h5>{{ employment.employed | fullName }}</h5>
    </v-card-title>
    <v-card-text>
      <p class="mt-n9" v-if="employment.employed.personalNumber">
        {{ employment.employed.personalNumber | formatPnr }}
      </p>
      <MessageBox>
        <h6 class="ma-0">Obs! Dessa inställningar visas på och påverkar Mina sidor.</h6>
      </MessageBox>

      <h6 class="mt-5">Rollbeskrivning</h6>
      <p>
        Denna text beskriver kortfattat vad personen arbetar med på företaget. Den påverkar inte personens behörigheter
        eller yrkesbevis utan är rent informativ för företagets medarbetare på Mina sidor. Exempel på rollbeskrivningar
        är “platschef” och “personalansvarig”.
      </p>
      <v-text-field data-cy="edit-employee-role" class="mt-n1" outlined v-model="employment.roleDescription"></v-text-field>

      <h6 class="mt-n3 special--color">Roller</h6>
      <div class="roles__box mt-n3 special--color">
        <p class="roles__box--info special--color mb-2">
          Den tilldelning av roller du gör här styr vilka behörigheter personen får på Mina sidor för detta företag.
        </p>

        <v-switch class="mt--2" v-model="employment.isAdministrator" label="Företagsadministratör (FTGADM)"></v-switch>
        <p class="roles__box--switches-text">
         Denna roll har fullständiga behörigheter att lägga till, ändra och radera användare och uppgifter om företaget på Mina sidor. 
          Rollen kan inte godkänna delmoment i e-böcker.
        </p>

        <div v-if="selectedCompany && selectedCompany.hasDepartments && employment">
          <v-switch v-model="isDepartmentAdministrator" label="Avdelningsadministratör (AVDADM)"></v-switch>
          <p class="roles__box--switches-text">
            Denna roll har fullständiga behörigheter lägga till, ändra och radera användare och uppgifter i en eller 
            flera av företagets avdelningar på Mina sidor. 
            Rollen kan inte godkänna delmoment i e-böcker.
            <MultiSelect
              class="roles__box--departments--multiselect"
              v-if="isDepartmentAdministrator"
              :list="departments"
              label="Avdelningar"
              v-model="employment.administratorDepartmentIds"
            />
          </p>

          <v-switch
            v-model="isDepartmentDistanceEducationAdministrator"
            label="Beställningsgodkännare (BG)"
          ></v-switch>

          <p class="roles__box--switches-text">
            Denna roll har behörighet att godkänna företagets beställningar av distansutbildningar på Mina sidor och mandat att ingå avtal med betalningsansvar.
            <MultiSelect
              class="roles__box--departments--multiselect"
              v-if="isDepartmentDistanceEducationAdministrator"
              :list="departments"
              label="Avdelningar"
              v-model="employment.distanceEducationAdministratorDepartmentIds"
            />
          </p>

          <v-switch v-model="isDepartmentEducationResponsible" label="Yrkesutbildningsansvarig (YUA)"></v-switch>
          <p class="roles__box--switches-text">
            Denna roll har behörighet att utse handledare, koppla lärlingar till handledare och visa alla e-böcker på företaget. Rollen kan inte godkänna delmoment i e-böcker. Rollen kan tilldela handledarroll till personal.
            <MultiSelect
              v-if="isDepartmentEducationResponsible"
              class="roles__box--departments--multiselect"
              :list="departments"
              label="Avdelningar"
              v-model="employment.educationResponsibleDepartmentIds"
            />
          </p>

          <v-switch v-model="isDepartmentInstructor" label="Handledare (HL)"></v-switch>
          <p class="roles__box--switches-text mb-4">
           Denna roll kräver yrkesbevis eller handledardispens. En person med denna roll har behörighet att handleda en eller flera lärlingar (beroende på yrke) under lärling- eller kvalificeringstid på företaget, visa sina lärlingars e-böcker och godkänna delmoment i sina lärlingars e-böcker.
            <MultiSelect
              class="roles__box--departments--multiselect"
              v-if="isDepartmentInstructor"
              :list="departments"
              label="Avdelningar"
              v-model="employment.instructorDepartmentIds"
            />
          </p>
        </div>
        <div v-else>
          <v-switch
            v-model="employment.isDistanceEducationAdministrator"
            label="Beställningsgodkännare (BG)"
          ></v-switch>
          <p class="roles__box--switches-text">
            Denna roll har behörighet att godkänna företagets beställningar av distansutbildningar på Mina sidor och mandat att ingå avtal med betalningsansvar.

          </p>

          <v-switch
            v-model="employment.isCompanyEducationResponsible"
            label="Yrkesutbildningsansvarig (YUA)"
          ></v-switch>
          <p class="roles__box--switches-text">
                       Denna roll har behörighet att utse handledare, koppla lärlingar till handledare och visa alla e-böcker på företaget. Rollen kan inte godkänna delmoment i e-böcker. Rollen kan tilldela handledarroll till personal.

          </p>

          <v-switch v-model="employment.isInstructor" label="Handledare (HL)"></v-switch>
          <p class="roles__box--switches-text mb-4">
          Denna roll kräver yrkesbevis eller handledardispens. En person med denna roll har behörighet att handleda en eller flera lärlingar (beroende på yrke) under lärling- eller kvalificeringstid på företaget, visa sina lärlingars e-böcker och godkänna delmoment i sina lärlingars e-böcker.

          </p>
        </div>
      </div>
      <h6>Koppla bort personen från företaget</h6>
      <p>
        Om du kopplar bort personen från företaget förlorar personen alla roller och behörigheter hen har hos detta
        företag.
      </p>
      <p class="bold">Bortkoppling går inte att ångra och sker omedelbart.</p>
      <v-btn color="secondary" class="mt-n1" @click="deleteEmployment" v-if="employment"
        ><v-icon>$sl-unlink</v-icon>KOPPLA BORT {{ employment.employed | fullName }} FRÅN
        {{ employment.company.name }}</v-btn
      >
    </v-card-text>
    <v-card-actions class="mt-12">
      <v-btn @click="updateEmployment()"> <v-icon>$sl-check</v-icon>Spara </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import {RulesMixin} from '@/components/mixins'
import MessageBox from '@/components/common/MessageBox.vue'
import MultiSelect from '@/components/common/SelectTypes/MultiSelect'
import find from 'lodash/find'
export default {
  name: 'EmploymentInformation',

  mixins: [RulesMixin],

  props: {
    //has employent id
    employmentId: {
      // `Number`
      type: Number,
      // The default value is: `0`
      default: 0
    }
  },
  components: {
    MessageBox,
    MultiSelect
  },
  data() {
    return {
      departments: [],
      employment: {},
      companyId: null,
      workplacesList: [],
      workplaces: [],
      isDepartmentAdministrator: false,
      isDepartmentInstructor: false,
      isDepartmentEducationResponsible: false,
      isDepartmentDistanceEducationAdministrator: false
    }
  },
  computed: {
    ...mapGetters('company', ['selectedCompany'])
  },
  created() {
    this.init()
  },

  methods: {
    init() {
      if (this.$route && this.$route.params && this.$route.params.id) {
        this.companyId = this.$route.params.id
      }
      this.fetchEmployment()
      this.getEmployments()
    },
    // @vuese
    //- show Employment or create new one
    fetchEmployment() {
      if (!this.employmentId) return
      API.getEmployment(this.employmentId)
        .then(res => {
          this.employment = res.data

          res.data && res.data.administratorDepartmentIds && res.data.administratorDepartmentIds.length > 0
            ? (this.isDepartmentAdministrator = true)
            : (this.isDepartmentAdministrator = false)
          res.data && res.data.instructorDepartmentIds && res.data.instructorDepartmentIds.length > 0
            ? (this.isDepartmentInstructor = true)
            : (this.isDepartmentInstructor = false)
          res.data &&
          res.data.educationResponsibleDepartmentIds &&
          res.data.educationResponsibleDepartmentIds.length > 0
            ? (this.isDepartmentEducationResponsible = true)
            : (this.isDepartmentEducationResponsible = false)
          res.data &&
          res.data.distanceEducationAdministratorDepartmentIds &&
          res.data.distanceEducationAdministratorDepartmentIds.length > 0
            ? (this.isDepartmentDistanceEducationAdministrator = true)
            : (this.isDepartmentDistanceEducationAdministrator = false)
        })
        .catch(error => {
          console.log(error)
        })
    },

    // @vuese
    // Deletes user
    async deleteEmployment() {
      const res = await this.$dialog.confirm({
        text: 'Bortkoppling går inte att ångra.',
        title: 'Är du säker på att du vill koppla bort denna person? ',
        actions: [
          {text: 'AVBRYT', key: false, icon: '$sl-cancel'},
          {text: 'KOPPLA BORT', key: true, icon: '$sl-delete', outlined: true}
        ]
      })

      if (res !== true) return

      API.deleteEmployment(this.employment.id)
        .then(response => {
          this.$events.fire('showInfo', 'Personen bortkopplad.')
          this.$emit('show-list', true)
        })
        .catch(error => {
          this.$events.fire('showError', 'Kunde inte koppla bort personen. Det kan t.ex. bero på att det är en handledare som är kopplad till en eller flera lärlingar.')
          console.log('There was an error:', error)
        })
    },
    updateEmployment() {
      if (!this.isDepartmentAdministrator) {
        this.employment.administratorDepartmentIds = []
      }
      if (!this.isDepartmentEducationResponsible) {
        this.employment.educationResponsibleDepartmentIds = []
      }
      if (!this.isDepartmentDistanceEducationAdministrator) {
        this.employment.distanceEducationAdministratorDepartmentIds = []
      }
      if (!this.isDepartmentInstructor) {
        this.employment.instructorDepartmentIds = []
      }
      API.setEmployment(this.employment)
        .then(response => {
          this.$events.fire('showInfo', 'Personen uppdaterad.')
          this.$emit('show-list', true)
        })
        .catch(error => {
          console.log('There was an error:', error.response)
        })
    },

    // @vuese
    //- Used to get all the  employements from the selected company
    getEmployments() {
      API.listDepartments(this.selectedCompany.id)
        .then(response => {
          response.data.forEach(d => {
            this.departments.push({
              id: d.id,
              name: d.name ? d.name : ''
            })
          })
        })
        .catch(error => {
          console.log('get departments', error)
        })
    }
  }
}
</script>


<style  scoped>
.mt--2 {
  margin-top: -2px !important;
}
.special--color {
  color: #3b3c3a !important;
}
</style>
